import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import { TrendingDown, TrendingUp, Wallet, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { MobileTransactionForm } from '../../component/finance/MobileFinance';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { dispatchCashFlowInvalidate } from '../../common/cashFlowEvents';
import { formatCurrency } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import { transactionListDateToIso } from '../../common/transactionDates';
import {
    amountFromRowToInput,
    readApiErrorMessage,
    settingRowSx,
    TxDrawerHeaderIcon,
    TxTypeCard,
    TxTypeLockNote,
    useStandaloneFieldSx
} from './shared';

// Formulário de transação — DESIGN-SYSTEM.md §5.6/§5.13, drawer lateral real
// (mesmo padrão de `pages/cashregisters/Form.js`: `drawer-overlay`/
// `drawer-box` viram o `Drawer` do MUI aberto por `pages/transactions/
// List.js`, `drawer-head`/`drawer-body`/`drawer-foot` viram os `Box`
// abaixo). Não há protótipo de página cheia dedicado (mesma decisão de
// "Formulário de caixa") — acessar `/transactions/new` diretamente (fora do
// drawer) redireciona para `/transactions`, onde o mesmo formulário roda
// embutido no drawer da Lista.
//
// Corpo do drawer (`prototipo-lista-transacoes.html`): radio-cards
// `tx-type-cards` Receita/Despesa (`TxTypeCard`, §5.13, mesmo padrão
// `:has(input:checked)` dos `move-card` de `prototipo-reposicao-
// estoque.html`) seguidos de Descrição, e Valor+Data lado a lado
// (`ff-grid`); ao selecionar Despesa, aparecem Vencimento e o toggle
// "Pagar pelo caixa agora" (`setting-row`). O protótipo não modela um campo
// de Observações no corpo do drawer (só Tipo/Descrição/Valor/Data/
// Vencimento/toggle) — removido daqui para bater exatamente com o desenho
// aprovado; o payload continua enviando `obs`/`observations` (string vazia)
// para não alterar o contrato com a API (o valor já era sempre vazio antes,
// já que `GET /transactions/query` também não devolve essa coluna — mesma
// lacuna documentada abaixo para `due_date`).
//
// **Dois modos, um só componente**: sem `transaction` = criar (`POST
// /transactions/revenue`|`/transactions/expense`, chamado por "Nova
// transação"). Com `transaction` = editar um lançamento existente — tipo
// **travado** (radio-cards desabilitados + `TxTypeLockNote`), chamado por
// "Editar" em `List.js` para Receita (qualquer status) ou Despesa já
// Prestado Contas (despesa Pendente/Vencido usa a variante compacta,
// `TransactionEditForm.js`). A edição usa o mesmo `PUT /transactions/{id}`
// já usado por `TransactionEditForm` — não é um endpoint novo. Esse
// endpoint (`transaction.Repository.
// UpdateLancamentoDespesaPendenteOuVencido`) só persiste quando o
// lançamento é uma despesa com status Pendente/Vencido; para Receita ou
// Despesa já Prestado Contas ele responde `409 ErrTransactionNotEditable`
// de propósito — o mesmo comportamento real de "Excluir"/`DELETE
// /transactions/{id}` (chamada real, falha graciosa com mensagem explícita
// em vez de fingir sucesso, documentado em `List.js`). Por isso editar uma
// Receita ou uma Despesa Prestado Contas por este formulário sempre mostra
// esse erro (com uma orientação contextual — "Voltar para pendente" para
// despesa já paga) em vez de salvar: é o comportamento real e esperado da
// API hoje, não um bug; liberar edição de verdade para esses dois casos
// exigiria uma mudança de contrato no backend, fora do escopo desta tarefa
// e que precisa de aprovação explícita.
//
// Vencimento (despesa): obrigatório ao criar, mas **opcional** ao editar —
// `GET /transactions/query` não devolve `due_date` (lacuna de backend já
// documentada em `List.js`/`TransactionEditForm.js`), então o campo sempre
// abre em branco numa edição; exigi-lo forçaria reescrever o vencimento
// (com um valor desconhecido) a cada edição. Em branco preserva o
// vencimento já cadastrado (mesma regra do backend usada por
// `TransactionEditForm`).
export default function TransactionForm({ embedded = false, transaction = null, onClose, onSuccess, onListRefresh }) {
    const navigate = useNavigate();
    const standaloneFieldSx = useStandaloneFieldSx();
    const isEdit = Boolean(transaction);
    // Shell mobile: `/transactions/new` abre o formulário em página cheia (protótipo
    // `financeiro/lancamento-form.html`) em vez de redirecionar para a lista com o drawer.
    const isMobileShell = useIsMobileShell();

    useEffect(() => {
        if (!embedded && !isMobileShell) {
            navigate('/transactions', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embedded]);

    // Mobile (novo lançamento): protótipo abre em "Despesa"; o padrão do desktop continua "Receita".
    const [txType, setTxType] = useState(
        transaction?.type === 'despesa' || (!transaction && isMobileShell) ? 'despesa' : 'receita'
    );
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');

    const todayIso = () => new Date().toISOString().split('T')[0];

    const optionalIsoDateSchema = Yup.string().test(
        'due-iso',
        'Data de vencimento inválida',
        (val) => !val || !String(val).trim() || /^\d{4}-\d{2}-\d{2}$/.test(String(val).trim())
    );

    const revenueValidationSchema = Yup.object({
        description: Yup.string()
            .required('Descrição é obrigatória')
            .min(3, 'Descrição deve ter pelo menos 3 caracteres'),
        date: Yup.string().required('Data é obrigatória'),
        amount: Yup.string()
            .required('Valor é obrigatório')
            .test('is-positive', 'Valor deve ser maior que zero', function (value) {
                if (!value) return false;
                const numericValue = parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.'));
                return numericValue > 0;
            })
    });

    const expenseValidationSchema = Yup.object({
        description: Yup.string()
            .required('Descrição é obrigatória')
            .min(3, 'Descrição deve ter pelo menos 3 caracteres'),
        date: Yup.string().required('Data de lançamento é obrigatória'),
        dueDate: isEdit ? optionalIsoDateSchema : Yup.string().required('Data de vencimento é obrigatória'),
        amount: Yup.string()
            .required('Valor é obrigatório')
            .test('is-positive', 'Valor deve ser maior que zero', function (value) {
                if (!value) return false;
                const numericValue = parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.'));
                return numericValue > 0;
            }),
        isPay: Yup.boolean()
    });

    const showError = (text) => {
        setMessage(text);
        setSeverity('error');
        setOpenSnackbar(true);
    };
    const showSuccess = (text) => {
        setMessage(text);
        setSeverity('success');
        setOpenSnackbar(true);
    };
    // Após criar: no mobile em página cheia volta para a lista (que mostra o aviso); senão avisa aqui.
    const finishCreated = (text) => {
        if (isMobileShell && !embedded) {
            navigate('/transactions', { state: { toast: text } });
            return;
        }
        showSuccess(text);
    };

    const revenueFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            description: isEdit && transaction.type === 'receita' ? (transaction.description || '') : '',
            date:
                isEdit && transaction.type === 'receita'
                    ? transactionListDateToIso(transaction.date_created) || todayIso()
                    : todayIso(),
            amount: isEdit && transaction.type === 'receita' ? amountFromRowToInput(transaction.amount) : ''
        },
        validationSchema: revenueValidationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                if (isEdit) {
                    const payload = {
                        description: values.description.trim(),
                        date: values.date,
                        amount: values.amount,
                        observations: ''
                    };
                    const res = await httpClient.put(`/transactions/${transaction.id}`, payload);
                    if (res.status === 204) {
                        if (embedded && onSuccess) {
                            onSuccess({ kind: 'revenue', edited: true });
                            return;
                        }
                        showSuccess('Receita atualizada com sucesso!');
                        return;
                    }
                    if (res.status === 409 || res.status === 404) await onListRefresh?.();
                    showError(
                        res.status === 409
                            ? (readApiErrorMessage(res) ||
                                  'Receitas não podem ser editadas depois de lançadas — o backend só permite alterar despesas Pendentes ou Vencidas.')
                            : readApiErrorMessage(res) || 'Não foi possível salvar as alterações.'
                    );
                    return;
                }

                const data = {
                    description: values.description,
                    date: values.date,
                    amount: values.amount,
                    obs: ''
                };
                const response = await httpClient.post('/transactions/revenue', data);
                if (response.status !== 201) {
                    showError(response.data?.error || 'Erro ao salvar receita');
                    return;
                }
                revenueFormik.resetForm();
                if (embedded && onSuccess) {
                    onSuccess({ kind: 'revenue' });
                    return;
                }
                finishCreated('Receita cadastrada com sucesso!');
            } catch (error) {
                showError(error.response?.data?.error || error.message || 'Erro ao salvar receita');
            } finally {
                setLoading(false);
            }
        }
    });

    const expenseFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            description: isEdit && transaction.type === 'despesa' ? (transaction.description || '') : '',
            date:
                isEdit && transaction.type === 'despesa'
                    ? transactionListDateToIso(transaction.date_created) || todayIso()
                    : todayIso(),
            dueDate:
                isEdit && transaction.type === 'despesa'
                    ? transactionListDateToIso(transaction.due_date ?? transaction.dueDate) || ''
                    : '',
            amount: isEdit && transaction.type === 'despesa' ? amountFromRowToInput(transaction.amount) : '',
            // Ao editar, só chegamos aqui com uma despesa já Prestado Contas
            // (despesa Pendente/Vencido usa a variante compacta) — o toggle
            // nasce marcado e fica travado (ver `settingRowSx`/nota abaixo).
            isPay: isEdit && transaction.type === 'despesa'
        },
        validationSchema: expenseValidationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                if (isEdit) {
                    const payload = {
                        description: values.description.trim(),
                        date: values.date,
                        amount: values.amount,
                        observations: ''
                    };
                    const due = values.dueDate && String(values.dueDate).trim();
                    if (due) payload.due_date = due;
                    const res = await httpClient.put(`/transactions/${transaction.id}`, payload);
                    if (res.status === 204) {
                        if (embedded && onSuccess) {
                            onSuccess({ kind: 'expense', edited: true });
                            return;
                        }
                        showSuccess('Despesa atualizada com sucesso!');
                        return;
                    }
                    if (res.status === 409 || res.status === 404) await onListRefresh?.();
                    showError(
                        res.status === 409
                            ? (readApiErrorMessage(res) ||
                                  'Esta despesa já foi paga e prestada contas — não pode mais ser editada por aqui. Use "Voltar para pendente" na lista para reabri-la.')
                            : readApiErrorMessage(res) || 'Não foi possível salvar as alterações.'
                    );
                    return;
                }

                const data = {
                    description: values.description,
                    date: values.date,
                    due_date: values.dueDate,
                    amount: values.amount,
                    observations: '',
                    is_pay: values.isPay
                };
                const response = await httpClient.post('/transactions/expense', data);
                if (response.status !== 201) {
                    showError(response.data?.error || 'Erro ao salvar despesa');
                    return;
                }
                if (values.isPay) {
                    dispatchCashFlowInvalidate();
                }
                expenseFormik.resetForm();
                if (embedded && onSuccess) {
                    onSuccess({ kind: 'expense', invalidateCashFlow: values.isPay });
                    return;
                }
                finishCreated('Despesa cadastrada com sucesso!');
            } catch (error) {
                showError(error.response?.data?.error || error.message || 'Erro ao salvar despesa');
            } finally {
                setLoading(false);
            }
        }
    });

    const handleCurrencyChange = (event, formik) => {
        formik.setFieldValue('amount', formatCurrency(event.target.value));
    };

    const closeDrawer = () => {
        if (embedded && onClose) {
            onClose();
        } else {
            navigate('/transactions');
        }
    };

    const handleSubmit = (e) => {
        e?.preventDefault?.();
        if (txType === 'despesa') {
            expenseFormik.handleSubmit();
        } else {
            revenueFormik.handleSubmit();
        }
    };

    const activeFormik = txType === 'despesa' ? expenseFormik : revenueFormik;
    // Ao editar uma despesa (só alcançável já Prestado Contas), o toggle é
    // sempre travado marcado — não existe caminho de edição completa para
    // uma despesa ainda Pendente (essa usa `TransactionEditForm`).
    const payNowLocked = isEdit && txType === 'despesa';

    const snackbarNode = (
        <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={() => setOpenSnackbar(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <Alert onClose={() => setOpenSnackbar(false)} severity={severity} variant="filled" sx={{ width: '100%' }}>
                {message}
            </Alert>
        </Snackbar>
    );

    if (isMobileShell) {
        const mobileForm = (
            <MobileTransactionForm
                formik={activeFormik}
                txType={txType}
                onTypeChange={setTxType}
                isEdit={isEdit}
                onCurrencyChange={(e) => handleCurrencyChange(e, activeFormik)}
                payNowLocked={payNowLocked}
                onSave={handleSubmit}
                onCancel={closeDrawer}
                loading={loading}
                embeddedTitle={embedded ? (isEdit ? 'Editar lançamento' : 'Novo lançamento') : undefined}
                onClose={closeDrawer}
            />
        );
        if (embedded) {
            return (
                <Box sx={{ height: '100%', overflowY: 'auto', p: '16px', backgroundColor: 'background.default' }}>
                    {mobileForm}
                    {snackbarNode}
                </Box>
            );
        }
        return (
            <PageScaffold
                titlePageTarget="Novo lançamento"
                links={[{ title: 'Lançamentos', link: '/transactions' }]}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={message}
                severity={severity}
            >
                {mobileForm}
            </PageScaffold>
        );
    }

    return (
        <>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'background.default' }}>
                {/* drawer-head */}
                <Box sx={{ p: '22px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <TxDrawerHeaderIcon type={txType} />
                        <Typography sx={{ fontSize: 17, fontWeight: 800, mb: '4px' }}>
                            {isEdit ? 'Editar lançamento' : 'Novo lançamento'}
                        </Typography>
                        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }} noWrap>
                            {isEdit
                                ? `Editando "${transaction?.description || ''}".`
                                : 'Registre uma receita ou despesa financeira.'}
                        </Typography>
                    </Box>
                    <IconButton onClick={closeDrawer} size="small" aria-label="Fechar">
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </Box>

                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    {/* drawer-body */}
                    <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                        <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '10px' }}>
                            Tipo do lançamento
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px', mb: isEdit ? '2px' : '20px' }}>
                            <TxTypeCard
                                selected={txType === 'receita'}
                                tone="success"
                                icon={TrendingUp}
                                title="Receita"
                                description="Um valor que entra no financeiro do negócio"
                                onSelect={() => setTxType('receita')}
                                disabled={isEdit}
                            />
                            <TxTypeCard
                                selected={txType === 'despesa'}
                                tone="error"
                                icon={TrendingDown}
                                title="Despesa"
                                description="Um valor que sai do financeiro do negócio"
                                onSelect={() => setTxType('despesa')}
                                disabled={isEdit}
                            />
                        </Box>
                        {isEdit && <TxTypeLockNote />}

                        <Box sx={{ mb: 2 }}>
                            <TextField
                                fullWidth
                                autoFocus
                                label="Descrição *"
                                name="description"
                                inputProps={{ maxLength: 120 }}
                                value={activeFormik.values.description}
                                onChange={activeFormik.handleChange}
                                onBlur={activeFormik.handleBlur}
                                error={activeFormik.touched.description && Boolean(activeFormik.errors.description)}
                                helperText={activeFormik.touched.description && activeFormik.errors.description}
                                variant="outlined"
                                sx={standaloneFieldSx}
                            />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <TextField
                                fullWidth
                                label="Valor (R$) *"
                                name="amount"
                                value={activeFormik.values.amount}
                                onChange={(e) => handleCurrencyChange(e, activeFormik)}
                                onBlur={activeFormik.handleBlur}
                                error={activeFormik.touched.amount && Boolean(activeFormik.errors.amount)}
                                helperText={activeFormik.touched.amount && activeFormik.errors.amount}
                                variant="outlined"
                                sx={standaloneFieldSx}
                            />
                            <TextField
                                fullWidth
                                label="Data *"
                                name="date"
                                type="date"
                                value={activeFormik.values.date}
                                onChange={activeFormik.handleChange}
                                onBlur={activeFormik.handleBlur}
                                error={activeFormik.touched.date && Boolean(activeFormik.errors.date)}
                                helperText={activeFormik.touched.date && activeFormik.errors.date}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={standaloneFieldSx}
                            />
                        </Box>

                        {txType === 'despesa' && (
                            <>
                                <Box sx={{ mb: 1 }}>
                                    <TextField
                                        fullWidth
                                        label={isEdit ? 'Vencimento' : 'Vencimento *'}
                                        name="dueDate"
                                        type="date"
                                        value={expenseFormik.values.dueDate}
                                        onChange={expenseFormik.handleChange}
                                        onBlur={expenseFormik.handleBlur}
                                        error={expenseFormik.touched.dueDate && Boolean(expenseFormik.errors.dueDate)}
                                        helperText={
                                            (expenseFormik.touched.dueDate && expenseFormik.errors.dueDate) ||
                                            (isEdit
                                                ? 'Deixe em branco para manter o vencimento já cadastrado (não devolvido pela listagem).'
                                                : undefined)
                                        }
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        sx={standaloneFieldSx}
                                    />
                                </Box>

                                <Box sx={{ ...settingRowSx(true), pt: '2px' }}>
                                    <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'background.subtle', color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Wallet size={16} strokeWidth={2} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Pagar pelo caixa agora</Typography>
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                            {payNowLocked
                                                ? 'Esta despesa já foi paga e está com prestação de contas registrada. Para reabri-la, use "Voltar para pendente" na lista.'
                                                : expenseFormik.values.isPay
                                                  ? 'Esta despesa já nasce como "Prestado Contas" (paga na hora).'
                                                  : 'Desativado, a despesa fica "Pendente" até você confirmar o pagamento depois.'}
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={expenseFormik.values.isPay}
                                        disabled={payNowLocked}
                                        onChange={(e) => expenseFormik.setFieldValue('isPay', e.target.checked)}
                                    />
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* drawer-foot */}
                    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0 }}>
                        <Button variant="outlined" onClick={closeDrawer} size="small" disabled={loading}>
                            Cancelar
                        </Button>
                        <Button type="submit" variant="contained" size="small" disabled={loading}>
                            {loading ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Salvar lançamento'}
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity={severity} variant="filled" sx={{ width: '100%' }}>
                    {message}
                </Alert>
            </Snackbar>
        </>
    );
}
